import { Box, Typography, IconButton } from "@mui/material";
import { 
    Description,
    VideoLibrary,
    StickyNote2,
    Download,
    MenuBook,
    PlayArrow
} from "@mui/icons-material";
import { Resource, ResourceType } from "../../../../models/resource";
import { useNavigate } from "react-router-dom";

function extractMainName(filePath: string): string {
    const fileName = filePath.split('/').pop();
    if (!fileName) return '';
    return fileName.replace('.json', ''); 
}

interface ResourcePreviewCardProps {
    classroom_id: string;
    collection_id: string;
    resource: Resource;
    onClick: () => void;
}

export default function ResourcePreviewCard({ classroom_id, collection_id, resource, onClick }: ResourcePreviewCardProps) {
    const navigate = useNavigate();

    const downloadResource = async () => {
        try {
            const response = await fetch(`https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${resource.url}`, {
                mode: 'cors'
            });

            const blob = await response.blob();

            const extension = resource.url.split('.').pop();
            const filename = `elms_${resource.title}.${extension}`;
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('File download failed:', error);
        }
    };

    const getResourceConfig = () => {
        switch (resource.type) {
            case 0: // NOTE
                return {
                    icon: <StickyNote2 sx={{ fontSize: 20, color: '#4f46e5' }} />,
                    accentColor: '#4f46e5',
                    actionIcon: <MenuBook sx={{ fontSize: 18, color: '#6b7280' }} />,
                    actionLabel: 'Read note'
                };
            case 1: // MEDIA
                return {
                    icon: <VideoLibrary sx={{ fontSize: 20, color: '#e11d48' }} />,
                    accentColor: '#e11d48',
                    actionIcon: <PlayArrow sx={{ fontSize: 18, color: '#6b7280' }} />,
                    actionLabel: 'Play media'
                };
            case 2: // DOCUMENT
            default:
                return {
                    icon: <Description sx={{ fontSize: 20, color: '#0891b2' }} />,
                    accentColor: '#0891b2',
                    actionIcon: <Download sx={{ fontSize: 18, color: '#6b7280' }} />,
                    actionLabel: 'Download document'
                };
        }
    };

    const config = getResourceConfig();

    const handleNavigation = () => {
        switch (resource.type) {
            case ResourceType.NOTE:
                navigate(`/classrooms/${classroom_id}/library/collections/${collection_id}/notes/?note_id=${resource.url}`)
                return;
        }
    }

    const handleQuickAction = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        switch (resource.type) {
            case ResourceType.NOTE:
                navigate(`/classrooms/${classroom_id}/library/collections/${collection_id}/notes/?note_id=${resource.url}`)
                break;
            case 1: // MEDIA
                onClick();
                break;
            case 2: // DOCUMENT
                downloadResource();
                break;
        }
    };
        
    return (  
        <Box
            sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid #f1f5f9',
                cursor: 'pointer',
                transition: 'all 0.2s ease-out',
                height: "64px",
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 3,
                '&:hover': {
                    backgroundColor: '#f8fafc',
                    borderColor: '#e2e8f0',
                }
            }}
            key={resource.id}
            onClick={onClick}
        >
            {/* Icon/Image */}
            <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                {resource.type === 1 && resource.url && !resource.url.endsWith(".mp4") && !resource.url.endsWith(".json") ? (
                    <img
                        src={`https://d1at3arxf72ma9.cloudfront.net/classrooms/${resource.classroom_id}/resources/${resource.url}`}
                        alt="resource"
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '6px'
                        }}
                    />
                ) : (
                    config.icon
                )}
            </Box>

            {/* Title */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                    variant="body1"
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {resource.title}
                </Typography>
            </Box>

            {/* Quick Action Button */}
            <IconButton
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    '&:hover': {
                        backgroundColor: '#f1f5f9',
                    }
                }}
                onClick={handleQuickAction}
                title={config.actionLabel}
            >
                {config.actionIcon}
            </IconButton>

            
        </Box>
    );
}